// @givmo/app-admin — Employees (spec §97): the roster, plus the loop around it.
//
// This file is the TABBED SHELL. It owns the page root testid, the PageHeader, the tab strip, and the one
// query both tabs need (`GET /admin/company` — the company's name, work-email domain, and DECLARED
// headcount). The tab bodies live beside it:
//
//   Roster  → `roster-tab.tsx`   the existing roster, moved verbatim (same JSX, same hooks, same testids)
//   Invites → `invites-tab.tsx`  enrollment rate, invite list + resend, bulk add, the announcement
//
// ROSTER IS THE DEFAULT TAB, AND THAT IS LOAD-BEARING. `employees.list.react.spec.ts` is a BLOCKING e2e
// spec that lands on /employees and immediately expects `employees-search` and `employees-empty-state`.
// Base UI renders the active panel, so with Roster default those ids are on screen at load exactly as
// before the split. Do not make Invites the default, and do not defer Roster behind an interaction.
//
// `?tab=invites` deep-links the Invites tab (validated in `routes.tsx`; anything unrecognized falls back
// to Roster). The dashboard's Participation panel and the utilization card's no-employees state both link
// here that way, so an admin who just saw "nobody is enrolled" arrives at the surface that fixes it.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  companiesGetCompanyDashboardOptions,
  companiesGetCompanyOptions,
} from '@givmo/api-client/query';
import { PageHeader, Tabs, testIdProp, type TabsValue } from '@givmo/design-system';
import { toEnrollmentRate } from './invites-decode';
import { InvitesTab } from './invites-tab';
import { RosterTab } from './roster-tab';

export type EmployeesTab = 'roster' | 'invites';

export function EmployeesPage({ initialTab = 'roster' }: { initialTab?: EmployeesTab }) {
  const [tab, setTab] = useState<TabsValue>(initialTab);

  // The enrollment rate needs BOTH halves, and they come from different reads:
  //   numerator   `active_employees` on the dashboard read — linked employees, counted live
  //   denominator `number_of_employees` on the company read — the ADMIN-DECLARED headcount
  // The dashboard read is the same query key the dashboard itself uses, so arriving here from that page
  // hits the cache rather than refetching.
  const company = useQuery(companiesGetCompanyOptions());
  const dashboard = useQuery(companiesGetCompanyDashboardOptions());
  const enrollment = useMemo(
    () => toEnrollmentRate(dashboard.data?.active_employees, company.data?.number_of_employees),
    [dashboard.data?.active_employees, company.data?.number_of_employees],
  );

  return (
    <div
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-employees')}
    >
      <PageHeader
        title="Manage Employees"
        subtitle="Your company's enrolled employees and their match eligibility."
      />

      <Tabs.Root value={tab} onValueChange={setTab} appearance="saas">
        <Tabs.List aria-label="Employees">
          <Tabs.Tab value="roster" testId="employees-tab-roster">
            Roster
          </Tabs.Tab>
          <Tabs.Tab value="invites" testId="employees-tab-invites">
            Invites
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="roster">
          <RosterTab />
        </Tabs.Panel>

        <Tabs.Panel value="invites">
          <InvitesTab
            enrollment={enrollment}
            enrollmentLoading={company.isPending || dashboard.isPending}
            enrollmentIsError={company.isError}
            onEnrollmentRetry={() => void company.refetch()}
            companyName={company.data?.name ?? 'your company'}
            companyDomain={company.data?.domain}
          />
        </Tabs.Panel>
      </Tabs.Root>
    </div>
  );
}
